import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stack,
  FormControl,
  Box,
} from '@mui/material'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { setDecimalPrecision } from 'utils/helpers'

interface SingleInvoiceAdjustmentProps {
  open: boolean
  onClose: () => void
  onSaveAdjustment: (adjustmentAmount: number, adjustmentNote: string) => void
  initialAdjustmentAmount?: number
  initialAdjustmentNote?: string
}

const FooterContent: React.FC<{ onClose: () => void; onSaveAdjustment: () => void }> = ({
  onClose,
  onSaveAdjustment,
}) => {
  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button variant="text" onClick={onClose} aria-label="Cancel" data-testid="cancel-button">
        CANCEL
      </Button>
      <Button
        variant="text"
        onClick={onSaveAdjustment}
        aria-label="Add Adjustments"
        data-testid="add-adjustments-button"
      >
        SAVE ADJUSTMENT
      </Button>
    </Stack>
  )
}

const SingleInvoiceAdjustment: React.FC<SingleInvoiceAdjustmentProps> = ({
  open,
  onClose,
  onSaveAdjustment,
  initialAdjustmentAmount = '0.00',
  initialAdjustmentNote = '',
}) => {
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>(initialAdjustmentAmount.toString())
  const [adjustmentNote, setAdjustmentNote] = useState(initialAdjustmentNote)

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || /^-?\d*\.?\d{0,2}$/.test(value)) {
      setAdjustmentAmount(value)
    }
  }, [])

  const handleSave = useCallback(() => {
    const numValue = parseFloat(adjustmentAmount) || 0
    onSaveAdjustment(parseFloat(numValue.toFixed(2)), adjustmentNote)
  }, [adjustmentAmount, adjustmentNote, onSaveAdjustment])

  useEffect(() => {
    setAdjustmentAmount(initialAdjustmentAmount.toString())
    setAdjustmentNote(initialAdjustmentNote)
  }, [open, initialAdjustmentAmount, initialAdjustmentNote])

  const dialogTitleSx = useMemo(
    () => ({
      fontFamily: 'Roboto',
      fontSize: 20,
      fontWeight: 500,
      lineHeight: '32px',
      letterSpacing: '0.15px',
      textAlign: 'left',
    }),
    [],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={dialogTitleSx}>
          Adjustment
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form">
          <FormControl fullWidth margin="normal">
            <TextField
              label="Adjustment Amount"
              id="adjustment-amount"
              type="text"
              value={
                adjustmentAmount === ''
                  ? ''
                  : setDecimalPrecision(adjustmentAmount, {
                      decimalPrecisionOverride: 2,
                      shouldFormatNumber: true,
                    })
              }
              onChange={handleChange}
              placeholder="0.00"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  allowNegative: true,
                  'data-testid': 'adjustment-amount-input',
                },
              }}
              fullWidth
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              label="Adjustment Note"
              id="adjustment-note"
              placeholder="Adjustment Note"
              value={adjustmentNote}
              onChange={e => setAdjustmentNote(e.target.value)}
              helperText="Optional"
              multiline
              rows={1}
              variant="outlined"
              fullWidth
              data-testid="adjustment-note-input"
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <FooterContent onClose={onClose} onSaveAdjustment={handleSave} />
      </DialogActions>
    </Dialog>
  )
}

export default SingleInvoiceAdjustment
